/**
 * Module Client #01 — Tableau de bord & KPIs
 * Source : Guide_Client_Baobab_Connect.pdf · Chapitre 2
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const IMG = (name) => `${BACKEND}/api/uploads/docs/html_assets/client/${name}`;

const moduleClientDashboard = {
  id: 'client-tableau-de-bord',
  population: 'client',
  order: 1,
  category: 'Pilotage',
  title: 'Tableau de bord & KPIs',
  subtitle: 'Lire et exploiter votre centre de pilotage en moins de 5 minutes',
  icon: 'BarChart3',
  duration: '15 min',
  level: 'Débutant',
  audience: 'Tous utilisateurs client',
  pdfUrl: '/api/uploads/docs/training/client-tableau-de-bord.pdf',
  slides: [
    { type: 'cover', eyebrow: 'Module 01 — Client', title: 'Tableau de bord & KPIs', subtitle: 'Votre centre de pilotage quotidien', icon: 'BarChart3', duration: '15 min', audience: 'Tous utilisateurs', level: 'Débutant' },
    { type: 'intro', title: 'Objectifs', intro: 'Le tableau de bord est votre point d\'entrée quotidien sur Baobab Connect. Il agrège vos chiffres clés et vous permet d\'agir en un clic.', objectives: [
      'Comprendre ce que représente chaque KPI affiché',
      'Filtrer la période d\'affichage (7j / 30j / 90j / personnalisé)',
      'Cliquer sur un KPI pour zoomer sur les opérations sous-jacentes',
      'Distinguer les opérations spot des opérations sous contrat',
      'Identifier rapidement les actions urgentes (retards, devis en attente)',
    ]},
    { type: 'concept', eyebrow: 'Vocabulaire', icon: 'BookOpen', title: 'Les KPIs principaux', lede: 'Cinq indicateurs centraux structurent votre dashboard :',
      bullets: [
        '🚛 Opérations totales : nombre global d\'opérations sur la période sélectionnée',
        '⏳ En cours : opérations entre la validation du devis et la livraison',
        '✅ Livrées : opérations terminées (livraison confirmée + facture)',
        '⚠️ En retard : opérations dont la date de livraison est dépassée',
        '💰 Coût total : montant cumulé HT des opérations affichées',
      ],
      note: 'Si vous avez des opérations sous contrat, une 2ᵉ ligne « Opérations sous contrat » s\'affiche avec ses propres KPIs.',
    },
    { type: 'screenshot', eyebrow: 'Anatomie du dashboard', title: 'Anatomie du tableau de bord',
      image: IMG('img_002_02_dashboard.png'),
      callouts: [
        { label: 'En-tête personnalisé', text: 'Salutation contextuelle + bouton « Nouvelle demande » accessible partout.', x: 18, y: 12 },
        { label: 'Sélecteur de période', text: 'Bascule rapidement entre 7j / 30j / 90j / personnalisé.', x: 80, y: 10 },
        { label: 'KPIs cliquables', text: 'Cliquez sur un KPI pour ouvrir la liste filtrée correspondante.', x: 30, y: 35 },
        { label: 'Graphique évolution', text: 'Volume et CA sur la période, avec courbes comparatives N-1.', x: 50, y: 65 },
      ],
    },
    { type: 'steps', eyebrow: 'Utilisation', title: 'Lire vos chiffres en 60 secondes', steps: [
      { title: 'Vérifier la période active', detail: 'En haut à droite : ajuster si nécessaire (mensuel, trimestriel...).' },
      { title: 'Scanner les KPIs principaux', detail: 'Total, En cours, Livrées, Retard, Coût — dans cet ordre.' },
      { title: 'Identifier les anomalies', detail: 'KPI rouge ou en hausse anormale → action immédiate.' },
      { title: 'Drill-down sur le KPI critique', detail: 'Un clic ouvre la liste filtrée.' },
      { title: 'Agir', detail: 'Créer une demande, relancer un devis, signaler un paiement.' },
    ]},
    { type: 'screenshot', eyebrow: 'Drill-down', title: 'Cliquer pour zoomer',
      image: IMG('img_005_05_operations.png'),
      callouts: [
        { label: 'Filtre auto', text: 'L\'URL passe en /operations?status=en_cours et le bon onglet s\'active.', x: 50, y: 15 },
        { label: 'Compteur', text: 'Vérifie que le nombre correspond au KPI cliqué.', x: 65, y: 28 },
        { label: 'Tri & filtres avancés', text: 'Affinez encore par date, montant, trajet.', x: 50, y: 45 },
      ],
    },
    { type: 'concept', eyebrow: 'Spot vs Contrat', icon: 'Lightbulb', title: 'Comprendre la distinction Spot / Contrat',
      lede: 'Vos chiffres sont organisés en 2 familles :',
      bullets: [
        '🟢 Demandes spot : transports ponctuels créés à la demande (≈ 80 % des PME).',
        '🟦 Opérations sous contrat : flux récurrents négociés sur 6 ou 12 mois.',
        'Les KPIs sont distincts pour éviter de mélanger pilotage ponctuel et contractuel.',
        'Cliquer sur un KPI spot → /operations | Cliquer sur un KPI contrat → /mes-contrats.',
      ],
      note: 'Un total combiné apparaît si vous avez les deux types pour vous donner la vue globale.',
    },
    { type: 'checklist', title: 'Rituel quotidien recommandé (5 min/jour)', items: [
      'Vérifier les opérations en retard',
      'Examiner les devis en attente de validation',
      'Consulter les factures arrivant à échéance',
      'Vérifier l\'évolution du CA vs N-1',
      'Identifier 1 action prioritaire pour la journée',
    ]},
    { type: 'comparison', title: 'Bonnes pratiques de lecture', do: [
      'Toujours vérifier la période active avant de lire les chiffres',
      'Comparer aux périodes précédentes pour détecter les tendances',
      'Cliquer pour comprendre — ne jamais se contenter du total',
      'Configurer les notifications pour être alerté hors-app',
    ], dont: [
      'Ne pas lire les chiffres bruts sans contexte temporel',
      'Ne pas ignorer les KPIs rouges (retards, anomalies)',
      'Ne pas confondre opérations spot et contrat',
      'Ne pas attendre la fin de mois pour traiter les anomalies',
    ]},
    { type: 'quiz', title: 'Quiz #1',
      question: 'Votre KPI « En retard » passe de 0 à 3. Quelle est la meilleure action ?',
      options: [
        'Attendre la fin de semaine pour voir si ça se résout seul',
        'Cliquer sur le KPI pour voir les 3 opérations concernées et agir',
        'Appeler le support en urgence',
        'Créer une nouvelle demande pour compenser',
      ],
      answerIndex: 1,
      explanation: 'Le drill-down (1 clic) vous montre immédiatement quelles opérations sont en retard pour que vous puissiez prendre la bonne décision : relancer, prolonger, contacter l\'opérateur.',
    },
    { type: 'quiz', title: 'Quiz #2',
      question: 'Vous voyez « Coût total : 12.5 M FCFA ». À quoi correspond ce chiffre ?',
      options: [
        'Au CA total réalisé toutes périodes confondues',
        'Au montant HT cumulé des opérations sur la période active du dashboard',
        'Au solde à payer à Baobab Logistics',
        'À la valeur totale de votre entreprise',
      ],
      answerIndex: 1,
      explanation: 'Le KPI affiche le cumul HT des opérations comprises dans la période sélectionnée. Changer la période recalcule ce chiffre.',
    },
    { type: 'takeaways', title: 'À retenir', items: [
      'Le dashboard se lit en 5 min/jour, pas plus.',
      'Toujours vérifier la période active avant lecture.',
      'Cliquer sur un KPI pour drill-down dans les opérations.',
      'Spot et Contrat sont séparés pour clarté du pilotage.',
      'Les KPIs rouges sont des signaux d\'action, pas des bulletins météo.',
    ]},
    { type: 'cta', title: 'Module 01 terminé !', subtitle: 'Vous savez piloter au quotidien. Continuez avec « Mes opérations » pour maîtriser le cycle complet.',
      actions: [
        { label: 'Ouvrir mon dashboard', href: '/dashboard' },
        { label: 'Module suivant : Mes opérations', href: '/docs/client/training/client-mes-operations' },
        { label: 'Retour catalogue', href: '/docs/client/training' },
      ],
    },
  ],
};

export default moduleClientDashboard;
